import {
    addOnlineBookingAgreementSignature,
    submitOnlineBookingNotesAndAgreementsThunk
} from 'actions/onlineBooking/OnlineBookingActions';
import { AgreementCard } from 'components/Agreements/Card/AgreementCard';
import { AgreementModal } from 'components/Agreements/SignModal/AgreementSignModal';
import ErrorList from 'components/Form/ErrorList/ErrorList';
import { Input } from 'components/Form/Input/Input';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { Button } from 'components/UI/Button/Button';
import { Spinner } from 'components/UI/Spinner/Spinner';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveAgreements } from 'reducers/agreements/AgreementsSelectors';
import {
    getOnlineBookingAgreementSignatures,
    getOnlineBookingLoading,
    getOnlineBookingTotalDeposit
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { Agreement, AgreementSignature } from 'types/Agreement';
import { AlertScreen } from 'types/Alert';

const AgreementsForm = ({ agreements }: { agreements: Array<Agreement> }) => {
    const dispatch = useDispatch();

    const agreementsSignatures = useSelector(getOnlineBookingAgreementSignatures);

    const [selectedAgreement, setSelectedAgreement] = React.useState<Agreement | null>(null);

    const signAgreementHandler = (signature: AgreementSignature) => {
        dispatch(addOnlineBookingAgreementSignature(signature));
        setSelectedAgreement(null);
    };

    return (
        <>
            <h1 className="h1 underlined w-full mt-6 mb-5">Agreements</h1>

            <div className="w-full flex flex-col gap-4">
                {agreements.map(agreement => (
                    <AgreementCard
                        key={agreement.id}
                        agreement={agreement}
                        onClick={setSelectedAgreement}
                        checked={agreementsSignatures.some(
                            signature => signature.agreementId === agreement.id
                        )}
                    />
                ))}
            </div>

            {selectedAgreement && (
                <AgreementModal
                    open={!!selectedAgreement?.id}
                    agreement={selectedAgreement}
                    onClose={() => setSelectedAgreement(null)}
                    onSign={signAgreementHandler}
                />
            )}
        </>
    );
};

export const OnlineBookingNotesAndAgreementsStep: React.FC = () => {
    const dispatch = useDispatch();
    const marketplace = useMarketplace();

    const agreements = useSelector(getActiveAgreements);
    const isLoading = useSelector(getOnlineBookingLoading);
    const agreementsSignatures = useSelector(getOnlineBookingAgreementSignatures);
    const depositAmount = useSelector(getOnlineBookingTotalDeposit);

    const [notes, setNotes] = React.useState('');

    const submitLabel =
        depositAmount > 0
            ? 'Continue'
            : marketplace.onlineBookingPreferences.requireBookingRequest
            ? 'Request Appointment'
            : 'Book Now';

    const isValid = React.useMemo(() => {
        return agreementsSignatures.length === agreements.length;
    }, [agreementsSignatures, agreements]);

    const submitHandler = () => {
        dispatch(submitOnlineBookingNotesAndAgreementsThunk(notes));
    };

    return (
        <>
            <LeftColumn contentClassName="flex flex-col flex-1">
                <h1 className="h1 underlined w-full mb-6">Booking notes</h1>

                {isLoading && (
                    <div className="w-full text-center">
                        <Spinner size={50} />
                    </div>
                )}

                {!isLoading && (
                    <>
                        <ErrorList screen={AlertScreen.OnlineBookingNotesAndAgreements} />

                        <Input
                            multiline
                            value={notes}
                            placeholder="Add any important notes & how you heard about us here!"
                            onChange={e => setNotes(e.target.value)}
                        />

                        {agreements.length > 0 && <AgreementsForm agreements={agreements} />}
                    </>
                )}
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    <Button fullWidth disabled={!isValid || isLoading} onClick={submitHandler}>
                        {submitLabel}
                    </Button>
                }
            >
                <div className="flex flex-col gap-5">
                    <MarketplaceInfo
                        inverted
                        marketplace={marketplace}
                        className="hide-on-mobile-and-tablet"
                    />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </>
    );
};
